import { create } from 'zustand'
import axios from 'axios'

/**
 * Sessão do console, contra o Upgrade ID.
 *
 * O login é `POST /token` com `grant_type: password` no Upgrade ID, que devolve
 * um par de tokens. O access token vai no `Authorization` das chamadas ao
 * coletor, que o verifica contra o JWKS de lá.
 *
 * A senha nunca chega ao coletor, e nunca é guardada aqui — vai direto do
 * formulário para o Upgrade ID e some. O que fica em `localStorage` é o token,
 * que expira sozinho.
 */

const UPGRADE_ID_URL = import.meta.env.VITE_UPGRADE_ID_URL || 'https://id.upgradese.com.br'
const CHAVE = 'ai-usage-console:sessao'

function carregarSessao() {
  try {
    return JSON.parse(localStorage.getItem(CHAVE) || 'null')
  } catch {
    // localStorage pode estar bloqueado (janela anônima, política do
    // navegador). Sem sessão é uma tela de login, não uma tela quebrada.
    return null
  }
}

function guardar(sessao) {
  try {
    if (sessao) localStorage.setItem(CHAVE, JSON.stringify(sessao))
    else localStorage.removeItem(CHAVE)
  } catch {
    // Não poder guardar significa relogar a cada aba. Incômodo, não erro.
  }
}

const inicial = carregarSessao()

export const useAuth = create((set) => ({
  token: inicial?.token ?? null,
  email: inicial?.email ?? null,

  async entrar(email, senha) {
    const { data } = await axios.post(`${UPGRADE_ID_URL}/token`, {
      grant_type: 'password',
      email,
      password: senha,
      product: 'AI_USAGE_CONSOLE',
    })

    const token = data.access_token ?? data.accessToken
    if (!token) throw new Error('O Upgrade ID não devolveu um access token.')

    const sessao = { token, email }
    guardar(sessao)
    set(sessao)
  },

  sair() {
    guardar(null)
    set({ token: null, email: null })
  },

  /**
   * Trocar a senha estando logado.
   *
   * Pede a senha atual, que é o que prova que quem está na tela é a pessoa e
   * não uma aba esquecida aberta. O Upgrade ID derruba TODAS as sessões ao
   * trocar, então o certo depois disso é sair: manter a tela aberta com um
   * token que já não renova só adia o login para o pior momento.
   */
  async trocarSenha(senhaAtual, senhaNova) {
    const { token } = useAuth.getState()
    await axios.post(
      `${UPGRADE_ID_URL}/identity/password`,
      { currentPassword: senhaAtual, newPassword: senhaNova },
      { headers: { Authorization: `Bearer ${token}` } },
    )
    guardar(null)
    set({ token: null, email: null })
  },

  /**
   * Pedir o link de redefinição.
   *
   * O Upgrade ID responde 204 exista o e-mail ou não — a tela também não pode
   * diferenciar os dois casos, senão o console vira um verificador de quem tem
   * conta nos sete produtos.
   */
  async pedirRedefinicao(email) {
    await axios.post(`${UPGRADE_ID_URL}/identity/password/forgot`, {
      email,
      product: 'AI_USAGE_CONSOLE',
    })
  },

  /** Usar o link. Uso único, 30 minutos. */
  async redefinir(token, senhaNova) {
    await axios.post(`${UPGRADE_ID_URL}/identity/password/reset`, {
      token,
      newPassword: senhaNova,
    })
  },
}))
